// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListActions from "@insite/content-library/Widgets/ProductList/ProductListActions";
import ProductListProductImage from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductListProductInformation from "@rc/Overrides/Widgets/ProductList/ProductListProductInformation";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = ProductCardSelections & HasProduct;

export interface ProductListProductCardStyles {
    searchScoreDataGridItem?: GridItemProps;
    sponsoredText?: TypographyPresentationProps;

    // xng-change
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
}

export const productCardStyles: ProductListProductCardStyles = {
    searchScoreDataGridItem: {
        width: 12,
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },

    // xng-change
    wrapper: {
        css: css`
            display: flex;
            align-items: flex-start;
            width: 100%;
        `,
    },
    imageWrapper: {
        css: css`
            width: 115px;
            min-width: 115px;
            margin-right: 20px;

            [class^="LazyImageStyle"] {
                height: auto !important;
            }
        `,
    },
    infoWrapper: {
        css: css`
            flex: 1 1 auto;
        `,
    },
    actionsWrapper: {
        css: css`
            width: 293px;
            min-width: 293px;
            padding-left: 20px;
            max-height: 174px;
        `,
    },
};

const styles = productCardStyles;

const ProductListProductCard: FC<Props> = props => {
    return (
        <StyledWrapper
            {...styles.wrapper}
            data-test-selector={`productListProductCard${props.product.id}`}
            className="product-card"
        >
            <StyledWrapper {...styles.imageWrapper}>
                {props.product.isSponsored && (
                    <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                )}
                <ProductListProductImage
                    showImage={props.showImage}
                    showCompare={false}
                    showImageBadge={props.showImageBadges}
                    showTextBadge={props.showTextBadges}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.infoWrapper}>
                <ProductListProductInformation
                    showBrand={props.showBrand}
                    showTitle={props.showTitle}
                    showPartNumbers={props.showPartNumbers}
                    showAvailability={props.showAvailability}
                    showAttributes={false}
                    showImageBadges={props.showImageBadges}
                    maxImageBadges={props.maximumNumberOfImageBadges}
                    showTextBadges={props.showTextBadges}
                    maxTextBadges={props.maximumNumberOfTextBadges}

                    // xng-change
                    showCompare={true}
                    showAddToList={true}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.actionsWrapper}>
                <ProductListActions showPrice={props.showPrice} showAddToList={false} />
            </StyledWrapper>
            <StyledWrapper {...styles.searchScoreDataGridItem}>
                <ProductSearchScoreData />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProduct(ProductListProductCard);
